import gql from 'graphql-tag';
import * as Sentry from '@sentry/nuxt';
import { MarketingBannerFragment, BenefitsPdpFragment } from '~/store/globalBanner.gql';
import { PaymentMethodsFragment } from '@/components/layout/footer/PaymentMethods';
import { CartNotificationFragment } from '@/components/cart/CartNotification';
import {
  CartShippingEntryFragment,
  CartDealsSettingsFragment,
  DeliveryDetailsSettingsFragment,
  HighDemandProductsSettingsFragment,
} from '@/stores/cart-settings';
import { MainNavTopLevelEntryFragment, HelpNavFragment } from '@/store/mainNav.gql';
import { AccountBenefitsFragment } from '@/store/app.gql';
import { AccountNotificationFragment } from '@/components/account/AccountNotifications.fragment';
import { AccountRoutinesFragment } from '@/components/account/AccountRoutineSlider.fragment';
import { AccountProductsFragment } from '@/components/account/AccountProductSlider.fragment';
import { CartProgressQuery } from '@/components/cart/CartProgress/CartProgress.gql';
import { FooterNavFragment } from '@/components/layout/footer/FooterNav';
import { LogisticPartnersFragment } from '@/components/layout/footer/LogisticPartners';
import { SocialMediaAccountsFragment } from '@/components/layout/footer/SocialMediaAccounts';
import { LegalNavFragment } from '@/components/layout/footer/LegalNav';
import { GlobalNewsletterSignupFormFragment } from '@/components/common/blocks/NewsletterSignupForm';

import { useMainNavStore } from '@/stores/main-nav';
import { useGlobalBannerStore } from '@/stores/global-banner';
import { useCartSettingsStore } from '@/stores/cart-settings';
import { useAccountStore } from '@/stores/account';
import { useCartStore } from '@/stores/cart';

// Combine app and footer queries into a single comprehensive query
const AppInitQuery = gql`
  query AppInitQuery($site: String!) {
    # App shell data
    mainNav: entries(section: "mainNav", site: [$site]) {
      ...MainNavTopLevelEntryFragment
    }
    globalBanner: globalSet(site: [$site], handle: "globalBanner") {
      ...MarketingBannerFragment
      ...BenefitsPdpFragment
    }
    paymentMethods: globalSet(site: [$site], handle: "paymentMethods") {
      ...PaymentMethodsFragment
    }
    cartSettings: globalSet(site: [$site], handle: "cartSettings") {
      ...CartNotificationFragment
      ...CartShippingEntryFragment
      ...CartDealsSettingsFragment
      ...HighDemandProductsSettingsFragment
      ...DeliveryDetailsSettingsFragment
    }
    helpNav: globalSet(site: [$site], handle: "helpNav") {
      ...HelpNavFragment
    }
    seomatic(uri: "__home__", site: $site, asArray: true) {
      metaSiteVarsContainer
    }
    account: globalSet(site: [$site], handle: "account") {
      ...AccountBenefitsFragment
      ...AccountNotificationFragment
      ...AccountRoutinesFragment
      ...AccountProductsFragment
      ... on account_GlobalSet {
        isAuthDisabled
        maintenanceMessage
      }
    }
    pickystory: globalSet(site: [$site], handle: "pickystory") {
      ... on pickystory_GlobalSet {
        enableCartXSellTeasers
        enableUpsellTeasers
        enableXSellTeasers
      }
    }
    contactWidget: globalSet(site: [$site], handle: "contactWidget") {
      ... on contactWidget_GlobalSet {
        heading
        teaser
        externalUrl
        globallyEnabled
        icon {
          kind
          url
          width
          height
        }
      }
    }

    # Footer data
    footerNav: globalSet(site: [$site], handle: "footerNav") {
      ...FooterNavFragment
    }
    logisticPartners: globalSet(site: [$site], handle: "logisticPartners") {
      ...LogisticPartnersFragment
    }
    socialMediaAccounts: globalSet(site: [$site], handle: "socialMediaAccounts") {
      ...SocialMediaAccountsFragment
    }
    legalNav: globalSet(site: [$site], handle: "legalNav") {
      ...LegalNavFragment
    }
    newsletterSignup: globalSet(site: [$site], handle: "newsletterSignup") {
      ...GlobalNewsletterSignupFormFragment
    }
  }
  ${AccountBenefitsFragment}
  ${AccountNotificationFragment}
  ${AccountProductsFragment}
  ${AccountRoutinesFragment}
  ${CartDealsSettingsFragment}
  ${CartNotificationFragment}
  ${CartShippingEntryFragment}
  ${DeliveryDetailsSettingsFragment}
  ${FooterNavFragment}
  ${HelpNavFragment}
  ${HighDemandProductsSettingsFragment}
  ${LegalNavFragment}
  ${LogisticPartnersFragment}
  ${MainNavTopLevelEntryFragment}
  ${PaymentMethodsFragment}
  ${SocialMediaAccountsFragment}
  ${GlobalNewsletterSignupFormFragment}
  ${MarketingBannerFragment}
  ${BenefitsPdpFragment}
`;

export const useAppStore = defineStore('app', {
  state: () => ({
    initialized: false,
    footerInitialized: false,
    footerData: {
      footerNav: null,
      paymentMethods: null,
      logisticPartners: null,
      socialMediaAccounts: null,
      legalNav: null,
      newsletterSignup: null,
    },
    siteIdentity: {},
    showUpsellTeasers: false,
    showXSellTeasers: false,
    showCartXSellTeasers: false,
  }),

  actions: {
    init({ enableUpsellTeasers, enableXSellTeasers, enableCartXSellTeasers }) {
      this.initialized = true;
      this.showUpsellTeasers = enableUpsellTeasers;
      this.showXSellTeasers = enableXSellTeasers;
      this.showCartXSellTeasers = enableCartXSellTeasers;
    },

    setFooterData(footerData) {
      this.footerInitialized = true;
      this.footerData = footerData;
    },

    setSiteIdentity(seomaticData) {
      try {
        const raw = JSON.parse(seomaticData?.metaSiteVarsContainer || '{}');
        const identity = raw.identity || {};
        const sameAsLinks = raw.sameAsLinks || {};

        this.siteIdentity = {
          siteName: raw.siteName || '',
          name: identity.genericName || '',
          description: identity.genericDescription || '',
          url: identity.genericUrl || '',
          logo: identity.genericImage || null,
          email: identity.genericEmail || '',
          telephone: identity.genericTelephone || '',
          address: {
            streetAddress: identity.genericStreetAddress || undefined,
            addressLocality: identity.genericAddressLocality || undefined,
            addressRegion: identity.genericAddressRegion || undefined,
            postalCode: identity.genericPostalCode || undefined,
            addressCountry: identity.genericAddressCountry || undefined,
          },
          sameAs: Object.values(sameAsLinks)
            .map((link) => link.url || link.account || '')
            .filter(Boolean),
        };
      } catch (e) {
        console.error('Error parsing SEOMatic site identity', e);
        this.siteIdentity = {};
      }
    },

    /**
     * initializes app shell and footer on ssr page load
     */
    async initShell() {
      const { $i18n, $craftRequest } = useNuxtApp();
      const runtimeConfig = useRuntimeConfig();

      // Get other stores
      const mainNavStore = useMainNavStore();
      const globalBannerStore = useGlobalBannerStore();
      const cartSettingsStore = useCartSettingsStore();
      const accountStore = useAccountStore();
      const cartStore = useCartStore();

      try {
        // Combined query that fetches both app shell and footer data
        const {
          // App shell data
          seomatic,
          mainNav,
          helpNav,
          globalBanner,
          paymentMethods,
          cartSettings,
          account,
          pickystory,
          contactWidget,
          // Footer data
          footerNav,
          logisticPartners,
          socialMediaAccounts,
          legalNav,
          newsletterSignup,
        } = await $craftRequest(AppInitQuery, {
          site: $i18n.locale.value,
        });

        // Initialize app shell data using other stores
        mainNavStore.init(mainNav);
        mainNavStore.setHelpNav(helpNav);
        mainNavStore.setBenefitsNav(account.benefitsBlocks);
        mainNavStore.setContactWidget(contactWidget);
        globalBannerStore.init(globalBanner);
        cartSettingsStore.init({ ...cartSettings, paymentMethods });
        cartSettingsStore.initCartDeals(cartSettings);
        accountStore.init(account, { loginDisabled: runtimeConfig.public.loginDisabled ?? false });

        // App Settings
        this.init(pickystory);
        this.setSiteIdentity(seomatic);

        // Initialize footer data at the same time
        const footerData = {
          footerNav,
          paymentMethods: paymentMethods.paymentMethods,
          logisticPartners,
          socialMediaAccounts,
          legalNav,
          newsletterSignup,
        };
        this.setFooterData(footerData);

        // Fetch additional cart data
        const response = await $craftRequest(CartProgressQuery, {
          site: $i18n.locale.value,
        });

        cartStore.setFreeShippingThreshold(response.globalSet.freeShippingThreshold ?? 0);
      } catch (e) {
        console.error('Error initializing app shell and footer', e);
        Sentry.captureException(e);
      }
    },
  },
});
